<template>
  <div class="payment_outer_container">
    <PaymentProcessing v-if="processing" :card="model" class="payment__processing" />
    <div v-else class="payment">
      <div class="payment__main">
        <CreditCard :card="model" :isFlipped="isFlipped" class="payment__credit-card" />
        <PaymentForm
          ref="paymentForm"
          :formError.sync="disabled"
          :model.sync="model"
          :amount="amount"
          @setFlipped="(flipped) => (isFlipped = flipped)"
        />
      </div>
      <div class="payment__footer" @mouseover="checkModel">
        <div class="payment__amount">
          <span>Valor Total</span>
          <span>
            {{ amount | toCurrency }}
            {{ getInstallmentsLabel }}
          </span>
        </div>
        <div v-if="showPaymentButton" @click="payment">
          <v-button
            :disabled="disabled"
            class="payment__action"
            text="Realizar pagamento"
            width="100%"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PaymentForm, CreditCard } from '@/components/payment';
import PaymentProcessing from './partials/PaymentProcessing';
import { Braspag } from '@/services/externals';
import { mapActions, mapState } from 'vuex';
import validateCpf from '@/utils/helpers/validation/validateCpf';
import validateHolder from '@/utils/helpers/validation/validateHolder';
import { dataLayersMixin } from '@/utils/mixins';

export default {
  name: 'Payment',
  mixins: [dataLayersMixin],
  components: {
    PaymentProcessing,
    CreditCard,
    PaymentForm,
  },

  data: () => ({
    model: undefined,
    disabled: true,
    isFlipped: false,
    processing: false,
  }),
  created() {
    this.mostCommonPagesAnalytics({
      eventType: 'funnel_step',
      currentStep: 'Dados do cartão',
    });
  },
  methods: {
    ...mapActions(['getAccessTokenByUnitId', 'creditCardProcess']),
    checkModel() {
      this.$refs.paymentForm.checkForm();
    },
    redirect(name, params = {}) {
      setTimeout(() => {
        this.$router.push({ name, params });
      }, 600);
    },
    async payment() {
      if (!this.disabled) {
        this.processing = true;

        try {
          const paymentToken = await this.getPaymentToken();
          const {
            holder: name,
            installments,
            type: paymentType,
            cpf: document,
            brand: cardBrand,
          } = this.model;

          const amount = parseFloat(this.amount);
          const processingCard = await this.creditCardProcess({
            admissionId: this.admission.id,
            amount,
            document,
            installments,
            name,
            cardBrand,
            email: this.getPatientEmail,
            paymentToken,
            paymentType,
          });

          if (processingCard.errors && processingCard.errors.length) {
            throw new Error(this.processingCard.errors[0].message);
          }

          this.redirect('payment-success', { card: this.model, installments: installments });
        } catch (e) {
          this.redirect('payment-denied', {
            amount: this.amount,
            installments: this.model.installments,
          });
        }
      }
    },
    async getPaymentToken() {
      try {
        const { accessToken: AccessToken } = await this.getAccessTokenByUnitId();

        const {
          holder: HolderName,
          cardNumber: RawNumber,
          expirationDate: Expiration,
          securityCode: SecurityCode,
        } = this.model;

        const { PaymentToken } = await Braspag.getPaymentToken({
          HolderName,
          RawNumber,
          Expiration,
          SecurityCode,
          AccessToken,
        });

        return PaymentToken;
      } catch (e) {
        throw new Error(e);
      }
    },
    amount() {
      return this.exams.reduce((p, c) => p + c.price, 0);
    },
  },
  filters: {
    toCurrency(value) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    },
  },
  computed: {
    ...mapState({
      admission: ({ currentAdmission }) => currentAdmission.data,
      exams: (state) => state.currentAdmission.data.medicalOrders[0].exams,
      mdmData: ({ mdm }) => mdm.data,
    }),
    showPaymentButton() {
      if (this.disabled) {
        return false;
      } else if (this.model) {
        const { cardNumber, holder, expirationDate, securityCode, cpf } = this.model;
        const validateCardNumber = cardNumber && !!cardNumber.length && cardNumber.length >= 14;
        const validateCardHolder =
          holder && !!holder.length && holder.length > 5 && validateHolder(holder);
        const validateExpDate =
          expirationDate && !!expirationDate.length && expirationDate.length > 4;
        const validateSecCode = securityCode && !!securityCode.length && securityCode.length >= 3;
        const validateCardCpf = cpf && !!cpf.length && cpf.length >= 11 && validateCpf(cpf);

        return (
          validateCardNumber &&
          validateCardHolder &&
          validateExpDate &&
          validateSecCode &&
          validateCardCpf
        );
      } else return false;
    },

    getPatientEmail() {
      const { contacts } = this.mdmData;

      let email = contacts.find((contact) => contact.contactType === 'EMAIL' && contact.principal);
      if (!email) {
        email = contacts.find((contact) => contact.contactType === 'EMAIL');
      }

      return email?.value || '';
    },

    getInstallmentsLabel() {
      return this.model?.installmentsLabel && typeof this.model.installmentsLabel === 'string'
        ? `(${this.model.installmentsLabel})`
        : '';
    },
  },
  beforeMount() {
    const { amount = undefined } = this.$route.params;
    this.amount = amount || 0.0;
  },
};
</script>

<style lang="scss" scoped>
.payment {
  margin: 0 auto;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;

  &,
  &__footer,
  &__header {
    background: #ffff;
  }

  &__header {
    position: relative;
    width: calc(100% - 32px);
    margin: 0 auto;
    top: 0;
  }

  &__main {
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  &__amount {
    display: flex;
    justify-content: space-between;
  }

  &__footer {
    width: 100%;
    padding: 16px 0;
  }

  &__action {
    margin-top: 16px;
    min-width: 100%;
  }

  &__credit-card {
    height: auto;
    min-height: 160px;
    max-height: 188px;
    max-width: 425px;
    padding: 0 6px;
    flex: 1;
  }

  &__processing {
    margin: 0 auto;
  }
}
</style>
<style lang="scss">
.credit-card-payment {
  .template {
    align-items: center;
    height: 100%;
    position: relative;

    &__content {
      width: 100%;
      justify-content: center;
      height: 100%;
      overflow: hidden;

      > div {
        width: 100%;
      }
    }
  }
}
</style>
